import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Divider } from "@itandi/itandi-bb-ui/Divider";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { InputDateTimev2 } from "@itandi/itandi-bb-ui/InputDateTimev2";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import type { ComponentProps } from "react";
import { useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import type { CompanyDealTodo } from "~/models/CompanyDealTodo";
import type { Deal } from "~/models/Deal";
import type { DealTodoEdit } from "~/models/DealTodo";
import type { Staff } from "~/models/Staff";
import { toDate } from "~/utils/DateUtil";

type Props = DeepReadonly<{
	deal: Deal | CompanyDealTodo["deal"];
	staffs: Staff[];
}>;

export function DealClientTodoModalForm({ deal, staffs }: Props) {
	const {
		setValue,
		register,
		watch,
		control,
		formState: { errors }
	} = useFormContext<DealTodoEdit>();
	const onStaffChange: NonNullable<
		ComponentProps<typeof SingleComboBox>["onChange"]
	> = useCallback(
		(value) => {
			if (!value) {
				setValue("staff", null);
			} else {
				setValue(
					"staff",
					staffs.find((staff) => staff.id === Number(value.value))!
				);
			}
		},
		[setValue, staffs]
	);
	const assignedStaff = watch("staff");
	return (
		<FormGroup variant="horizontal">
			<FormControl label="完了">
				<InputCheckBox
					{...register("completed")}
					checked={watch("completed")}
					label="TODOを完了にする"
				/>
			</FormControl>
			<Divider />
			<FormControl chip="必須" isInvalid={!!errors.title} label="タイトル">
				<Input {...register("title")} />
				<FormControlText error={errors.title?.message} />
			</FormControl>
			<Divider />
			<FormControl label="担当者">
				<SingleComboBox
					maxMenuHeight={200}
					onChange={onStaffChange}
					options={staffs.map((staff) => ({
						label: staff.name,
						value: String(staff.id)
					}))}
					placeholder="選択してください"
					value={
						assignedStaff !== null
							? { label: assignedStaff.name, value: String(assignedStaff.id) }
							: { label: deal.staff.name, value: String(deal.staff.id) }
					}
				/>
			</FormControl>
			<Divider />
			<FormControl label="対応期限">
				<Controller
					control={control}
					name="dueDate"
					render={({ field: { value, onChange } }) => (
						<InputDateTimev2
							onChange={(date): void => {
								onChange(date?.toISOString() ?? null);
							}}
							value={toDate(value)}
						/>
					)}
				/>
			</FormControl>
			<Divider />
			<FormControl isInvalid={!!errors.memo} label="メモ">
				<InputText rows={3} {...register("memo")} />
				<FormControlText error={errors.memo?.message} />
			</FormControl>
		</FormGroup>
	);
}
